import React, { useEffect, useState} from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './msalConfig';
import logo from './logo.png';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import ChartsDashboard from './ChartsDashboard';
import SavingsDashboard from './SavingsDashboard';
import AdminLogin from './admin-login';
import AdminDashboard from './admin-dashboard';
import AppLogin from './login';
import PrivateRoute from "./PrivateRoute";
import { logout } from './utils/logout';



const msalInstance = new PublicClientApplication(msalConfig);

function transformLicenseData(rawData, pricingData) {
  return rawData
    .map(row => ({
      "License Type": row.skuPartNumber,
      "SkuId": row.skuId,
      "Total Licenses": parseInt(row.prepaidUnits?.enabled || 0, 10),
      "Assigned Licenses": parseInt(row.consumedUnits || 0, 10),
    }))
    .map(row => ({
      ...row,
      "Unassigned Licenses": row["Total Licenses"] - row["Assigned Licenses"]
    }))
    .map(row => {
      // ✅ Match pricing info, including Friendly Name
      const pricing = pricingData.find(p => p.SkuPartNumber === row["License Type"]) || {};
      return {
        ...row,
        ...pricing,
        "Friendly Name": pricing["Friendly Name"] || row["License Type"] // <- fallback if missing
      };
    })
    .map(row => ({
      ...row,
      "Unit Monthly Cost": parseFloat(row["Monthly Cost (USD)"] || 0),
      "Unit Monthly Cost with 12% Discount": parseFloat(row["Monthly Cost Discount (12%)"] || 0),
      "Unit Annual Cost": parseFloat(row["Annual Cost (USD)"] || 0),
      "Unit Annual Cost with 12% Discount": parseFloat(row["Annual Cost Discount (12%)"] || 0)
    }))
    .map(row => ({
      ...row,
      "Unassigned Cost (Annual)": row["Unassigned Licenses"] * row["Unit Annual Cost with 12% Discount"],
      "Assigned Cost (Annual)": row["Assigned Licenses"] * row["Unit Annual Cost with 12% Discount"],
      "Assigned Cost (Monthly)": row["Assigned Licenses"] * row["Unit Monthly Cost with 12% Discount"],
      "Unassigned Cost (Monthly)": row["Unassigned Licenses"] * row["Unit Monthly Cost with 12% Discount"]
    }))
    .filter(row => row["Unassigned Cost (Annual)"] != null)
    .map(row => ({
      // ✅ Include Friendly Name in final dataset
      "License Type": row["License Type"],
      "Friendly Name": row["Friendly Name"],
      "Total Licenses": row["Total Licenses"],
      "Assigned Licenses": row["Assigned Licenses"],
      "Unassigned Licenses": row["Unassigned Licenses"],
      "Unassigned Cost (Annual)": row["Unassigned Cost (Annual)"],
      "Assigned Cost (Annual)": row["Assigned Cost (Annual)"],
      "Assigned Cost (Monthly)": row["Assigned Cost (Monthly)"],
      "Unassigned Cost (Monthly)": row["Unassigned Cost (Monthly)"]
    }));
}


function LandingPage({ setLicenseData }) {
  const [isMsalReady, setIsMsalReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [startProgress, setStartProgress] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    msalInstance.clearCache(); // ⛔ Clear MSAL memory and sessionStorage
    sessionStorage.clear();    // ⛔ Also clear any other app state in session
    msalInstance.initialize().then(() => setIsMsalReady(true));
  }, []);
  

  useEffect(() => {
    if (startProgress) {
      let current = 0;
      const duration = 6500;
      const interval = 100;
      const step = 100 / (duration / interval);

      const timer = setInterval(() => {
        current += step;
        const next = Math.min(current, 100);
        setProgress(next);
        if (next >= 100) {
          clearInterval(timer);
          navigate('/dashboard');
        }
      }, interval);
    }
  }, [startProgress, navigate]);

  const handleLoginAndFetch = async () => {
    try {
      const loginResponse = await msalInstance.loginPopup(loginRequest); // prompt: "login" forces re-auth
      const accessToken = loginResponse.accessToken;
  
      // Fetch license data...
      const res = await fetch('https://fastapi-backend-789068808649.us-central1.run.app/graph/licenses', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
  
      const rawGraphData = await res.json();
      const pricingText = await fetch('/license_pricing.csv').then(res => res.text());
      const pricingData = Papa.parse(pricingText, { header: true }).data;
  
      const processed = transformLicenseData(rawGraphData.value, pricingData);
      setLicenseData(processed);
      setStartProgress(true);
    } catch (err) {
      console.error("❌ Login or data processing failed:", err);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9f9f9', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px' }}>
        <img src={logo} alt="Logo" style={{ height: '96px' }} />
        <button
          onClick={() => logout(navigate)}
          style={{
            marginLeft: 'auto',
            backgroundColor: '#CD0029',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Logout
        </button>
      </div>

      <main style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '30px' }}>
        <div style={{
          padding: '60px 80px',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
          textAlign: 'center',
          minWidth: '500px',
          fontFamily: 'Inter, sans-serif'
        }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '30px' }}>Welcome to Power Audit</h1>

          {!startProgress && (
            <button
              onClick={handleLoginAndFetch}
              disabled={!isMsalReady}
              style={{
                padding: '14px 28px',
                fontSize: '16px',
                backgroundColor: '#02AD24',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isMsalReady ? 'pointer' : 'not-allowed',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
              }}
            >
              Authenticate to Microsoft
            </button>
          )}

          {startProgress && (
            <div style={{
              marginTop: '30px',
              height: '24px',
              width: '100%',
              backgroundColor: '#e0e0e0',
              borderRadius: '6px',
              position: 'relative',
              fontFamily: 'Inter, sans-serif'
            }}>
              <div
                style={{
                  height: '100%',
                  width: `${progress}%`,
                  backgroundColor: '#02AD24',
                  transition: 'width 0.1s linear',
                  borderRadius: '6px'
                }}
              />
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                color: '#fff',
                fontSize: '13px'
              }}>
                {Math.round(progress)}%
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function App() {
  const [licenseData, setLicenseData] = useState(null);

  return (
    <Routes>
      <Route path="/" element={
        <PrivateRoute>
          <LandingPage setLicenseData={setLicenseData} />
        </PrivateRoute>
      } />
      <Route path="/dashboard" element={
        <PrivateRoute>
          <ChartsDashboard licenseData={licenseData} />
        </PrivateRoute>
      } />
      <Route path="/savings" element={
        <PrivateRoute>
          <SavingsDashboard licenseData={licenseData} />
        </PrivateRoute>
      } />
      
      {/* Public routes */}
      <Route path="/app-login" element={<AppLogin />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
    </Routes>
  );
}

export default App;
